import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Button,
  Divider,
  Dropdown,
  Empty,
  Input,
  List,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Typography,
  message
} from 'antd';
import type { MenuProps } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  EyeOutlined,
  FileTextOutlined,
  MoreOutlined,
  SettingOutlined,
  ShareAltOutlined,
  UserAddOutlined,
  UserDeleteOutlined
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LimsNavIcon } from '../../components/LimsNavIcon';
import { useAuth } from '../../contexts/AuthContext';
import { getDemoExperimentRecords, getDemoSharedNotebookFolders } from '../../demo/demoBrowseMocks';
import {
  experimentRecordService,
  ExperimentDiscipline,
  ExperimentRecord,
  ExperimentStatus
} from '../../services/experimentRecords/experimentRecordService';
import {
  sharedExperimentNotebookService,
  SharedExperimentNotebookFolder,
  SharedExperimentNotebookItem,
  SharedExperimentNotebookPermissions
} from '../../services/experimentRecords/sharedExperimentNotebookService';
import '../../styles/literature-stitch.css';
import '../../styles/experiment-records-stitch.css';
import '../MemberManagement.css';

const { Text, Title } = Typography;
const { TextArea } = Input;

const ALL_FOLDERS_VALUE = '';

const DISCIPLINE_LABEL: Record<ExperimentDiscipline, string> = {
  chemistry: '化学',
  biology: '生物',
  materials: '材料',
  general: '综合'
};

const STATUS_LABEL: Record<ExperimentStatus, string> = {
  draft: '草稿',
  final: '已定稿'
};

type FolderRow = SharedExperimentNotebookFolder & { isOwner: boolean };

type SharedFolderRef = { folderId: string; folderName: string };

type DisplayExperimentRecord = ExperimentRecord & {
  _sharedFolderRefs?: SharedFolderRef[];
  _sharedFolderHint?: string;
};

function formatTime(iso: string | undefined) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('zh-CN', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return '—';
  }
}

function formatRelativeUpdated(iso: string | undefined): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso).getTime();
    const now = Date.now();
    const diffSec = Math.max(0, Math.floor((now - d) / 1000));
    if (diffSec < 60) return `${diffSec} 秒前`;
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin} 分钟前`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr} 小时前`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 45) return `${diffDay} 天前`;
    return formatTime(iso);
  } catch {
    return formatTime(iso);
  }
}

function recordSnippet(rec: ExperimentRecord): string {
  const s = (rec.summary || '').trim();
  if (s) return s.slice(0, 220);
  const strip = (rec.content || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  return strip.slice(0, 220) || '（暂无摘要）';
}

function itemToRecord(it: SharedExperimentNotebookItem): ExperimentRecord | null {
  const r = it.experimentRecord;
  if (r && typeof r === 'object' && '_id' in r) return r as ExperimentRecord;
  return null;
}

const SharedExperimentNotebooks: React.FC = () => {
  const navigate = useNavigate();
  const { isDemoBrowseOnly } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const [folders, setFolders] = useState<FolderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [displayRecords, setDisplayRecords] = useState<DisplayExperimentRecord[]>([]);
  const [recLoading, setRecLoading] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createDesc, setCreateDesc] = useState('');
  const [creating, setCreating] = useState(false);

  const [addUserOpen, setAddUserOpen] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [selectedFolder, setSelectedFolder] = useState<FolderRow | null>(null);

  const [addRecOpen, setAddRecOpen] = useState(false);
  const [allRecords, setAllRecords] = useState<ExperimentRecord[]>([]);
  const [selectedRecordIds, setSelectedRecordIds] = useState<string[]>([]);
  const [loadingRecords, setLoadingRecords] = useState(false);

  const [permOpen, setPermOpen] = useState(false);
  const [permissions, setPermissions] = useState<SharedExperimentNotebookPermissions>({
    canAddRecords: false,
    canRemoveRecords: false,
    canViewUsers: false,
    canEditRecords: false
  });

  const setFolderFilter = useCallback(
    (id: string | null) => {
      setSelectedFolderId(id);
      if (id) setSearchParams({ folder: id }, { replace: true });
      else setSearchParams({}, { replace: true });
    },
    [setSearchParams]
  );

  useEffect(() => {
    const onR = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onR);
    return () => window.removeEventListener('resize', onR);
  }, []);

  const activeFolder = useMemo(
    () => (selectedFolderId ? folders.find((f) => f._id === selectedFolderId) ?? null : null),
    [folders, selectedFolderId]
  );

  const fetchFolders = useCallback(async () => {
    setLoading(true);
    try {
      if (isDemoBrowseOnly) {
        const merged: FolderRow[] = getDemoSharedNotebookFolders().map((f) => ({ ...f, isOwner: true }));
        setFolders(merged);
        return;
      }
      const [mine, shared] = await Promise.all([
        sharedExperimentNotebookService.getMyFolders(),
        sharedExperimentNotebookService.getSharedWithMeFolders()
      ]);
      const merged: FolderRow[] = [
        ...mine.map((f) => ({ ...f, isOwner: true })),
        ...shared.map((f) => ({ ...f, isOwner: false }))
      ];
      setFolders(merged);
    } catch (e: unknown) {
      message.error((e as Error).message || '获取共享夹失败');
    } finally {
      setLoading(false);
    }
  }, [isDemoBrowseOnly]);

  useEffect(() => {
    void fetchFolders();
  }, [fetchFolders]);

  useEffect(() => {
    if (folders.length === 0) {
      setSelectedFolderId(null);
      return;
    }
    const fp = searchParams.get('folder');
    if (!fp) {
      setSelectedFolderId(null);
      return;
    }
    if (!folders.some((x) => x._id === fp)) {
      setSearchParams({}, { replace: true });
      setSelectedFolderId(null);
      return;
    }
    setSelectedFolderId(fp);
  }, [searchParams, folders, setSearchParams]);

  const loadFolderRecords = useCallback(async () => {
    if (folders.length === 0) {
      setDisplayRecords([]);
      return;
    }
    setRecLoading(true);
    try {
      if (isDemoBrowseOnly) {
        const demoRecs = getDemoExperimentRecords();
        const folder = folders[0];
        const ref: SharedFolderRef = { folderId: folder._id, folderName: folder.name };
        if (!selectedFolderId) {
          setDisplayRecords(
            demoRecs.map((record) => ({
              ...record,
              _sharedFolderRefs: [ref],
              _sharedFolderHint: ref.folderName
            }))
          );
        } else {
          setDisplayRecords(demoRecs.map((record) => ({ ...record })));
        }
        return;
      }
      if (!selectedFolderId) {
        const results = await Promise.all(
          folders.map((f) => sharedExperimentNotebookService.getFolderRecords(f._id))
        );
        const map = new Map<string, { record: ExperimentRecord; refs: SharedFolderRef[] }>();
        results.forEach((data, idx) => {
          const folder = folders[idx];
          const ref: SharedFolderRef = { folderId: folder._id, folderName: folder.name };
          for (const it of data.items) {
            const rec = itemToRecord(it);
            if (!rec?._id) continue;
            const cur = map.get(rec._id);
            if (cur) {
              if (!cur.refs.some((r) => r.folderId === ref.folderId)) cur.refs.push(ref);
            } else {
              map.set(rec._id, { record: rec, refs: [ref] });
            }
          }
        });
        setDisplayRecords(
          Array.from(map.values()).map(({ record, refs }) => {
            const names = refs.map((r) => r.folderName);
            return {
              ...record,
              _sharedFolderRefs: refs,
              _sharedFolderHint: names.length > 1 ? names.join('、') : names[0]
            };
          })
        );
      } else {
        const data = await sharedExperimentNotebookService.getFolderRecords(selectedFolderId);
        const rows: DisplayExperimentRecord[] = [];
        for (const it of data.items) {
          const rec = itemToRecord(it);
          if (rec) rows.push({ ...rec });
        }
        setDisplayRecords(rows);
      }
    } catch (e: unknown) {
      message.error((e as Error).message || '加载共享记录失败');
      setDisplayRecords([]);
    } finally {
      setRecLoading(false);
    }
  }, [folders, selectedFolderId, isDemoBrowseOnly]);

  useEffect(() => {
    void loadFolderRecords();
  }, [loadFolderRecords]);

  const handleCreate = async () => {
    const n = createName.trim();
    if (!n) {
      message.warning('请输入共享夹名称');
      return;
    }
    setCreating(true);
    try {
      const f = await sharedExperimentNotebookService.createFolder({ name: n, description: createDesc.trim() });
      message.success('创建成功');
      setCreateOpen(false);
      setCreateName('');
      setCreateDesc('');
      await fetchFolders();
      setFolderFilter(f._id);
    } catch (e: unknown) {
      message.error((e as Error).message || '创建失败');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteFolder = async (id: string) => {
    try {
      await sharedExperimentNotebookService.deleteFolder(id);
      message.success('删除成功');
      if (selectedFolderId === id) setFolderFilter(null);
      await fetchFolders();
    } catch (e: unknown) {
      message.error((e as Error).message || '删除失败');
    }
  };

  const handleInviteUser = async () => {
    if (!userEmail.trim()) {
      message.warning('请输入用户邮箱');
      return;
    }
    if (!selectedFolder) return;
    try {
      await sharedExperimentNotebookService.inviteFolderUser(selectedFolder._id, userEmail.trim());
      message.success('邀请已发送，对方可在顶部通知或审批管理 → 待办事项中处理');
      setAddUserOpen(false);
      setUserEmail('');
      await fetchFolders();
    } catch (e: unknown) {
      message.error((e as Error).message || '发送邀请失败');
    }
  };

  const handleRemoveUser = async (folderId: string, userId: string) => {
    try {
      await sharedExperimentNotebookService.removeFolderUser(folderId, userId);
      message.success('已移除');
      await fetchFolders();
    } catch (e: unknown) {
      message.error((e as Error).message || '移除失败');
    }
  };

  const handleOpenAddRecords = async (folder: FolderRow) => {
    setSelectedFolder(folder);
    setLoadingRecords(true);
    try {
      const [listRes, folderRes] = await Promise.all([
        experimentRecordService.getList({ page: 1, limit: 200, sortBy: 'updatedAt', sortOrder: 'desc' }),
        sharedExperimentNotebookService.getFolderRecords(folder._id)
      ]);
      setAllRecords(listRes.experimentRecords);
      const existing = folderRes.items
        .map((it) => itemToRecord(it)?._id)
        .filter((x): x is string => Boolean(x));
      setSelectedRecordIds(existing);
      setAddRecOpen(true);
    } catch (e: unknown) {
      message.error((e as Error).message || '获取记录列表失败');
    } finally {
      setLoadingRecords(false);
    }
  };

  const handleAddRecords = async () => {
    if (!selectedFolder) return;
    const folderRes = await sharedExperimentNotebookService.getFolderRecords(selectedFolder._id);
    const existingIds = new Set(
      folderRes.items.map((it) => itemToRecord(it)?._id).filter(Boolean) as string[]
    );
    const selected = new Set(selectedRecordIds);
    const toAdd = selectedRecordIds.filter((id) => !existingIds.has(id));
    const toRemove = Array.from(existingIds).filter((id) => !selected.has(id));

    if (toAdd.length === 0 && toRemove.length === 0) {
      message.info('没有变更');
      setAddRecOpen(false);
      return;
    }
    try {
      for (const id of toRemove) {
        await sharedExperimentNotebookService.removeRecordFromFolder(selectedFolder._id, id);
      }
      if (toAdd.length > 0) {
        await sharedExperimentNotebookService.addRecordsToFolder(selectedFolder._id, toAdd);
      }
      message.success(toAdd.length && toRemove.length ? '已更新共享夹笔记' : toAdd.length ? '添加成功' : '已移除所选笔记');
      setAddRecOpen(false);
      setSelectedRecordIds([]);
      await fetchFolders();
      await loadFolderRecords();
    } catch (e: unknown) {
      message.error((e as Error).message || '保存失败');
    }
  };

  const handleOpenPermissions = (folder: FolderRow) => {
    setSelectedFolder(folder);
    const p = folder.permissions;
    setPermissions({
      canAddRecords: p?.canAddRecords ?? false,
      canRemoveRecords: p?.canRemoveRecords ?? false,
      canViewUsers: p?.canViewUsers ?? false,
      canEditRecords: p?.canEditRecords ?? false
    });
    setPermOpen(true);
  };

  const handleSavePermissions = async () => {
    if (!selectedFolder) return;
    try {
      await sharedExperimentNotebookService.updateFolder(selectedFolder._id, { permissions });
      message.success('权限设置已保存');
      setPermOpen(false);
      await fetchFolders();
    } catch (e: unknown) {
      message.error((e as Error).message || '保存失败');
    }
  };

  const canRemoveInFolder = useCallback(
    (folderId: string) => {
      const folder = folders.find((f) => f._id === folderId);
      if (!folder) return false;
      if (folder.isOwner) return true;
      return !!folder.permissions?.canRemoveRecords;
    },
    [folders]
  );

  const removeRecordFromFolder = async (folderId: string, recordId: string) => {
    await sharedExperimentNotebookService.removeRecordFromFolder(folderId, recordId);
  };

  const handleRemoveSingle = async (recordId: string, folderId: string) => {
    try {
      await removeRecordFromFolder(folderId, recordId);
      message.success('已移除');
      await loadFolderRecords();
      await fetchFolders();
    } catch (e: unknown) {
      message.error((e as Error).message || '移除失败');
    }
  };

  const handleRemoveAllRefs = async (record: DisplayExperimentRecord) => {
    const refs = record._sharedFolderRefs?.filter((r) => canRemoveInFolder(r.folderId)) ?? [];
    if (refs.length === 0) {
      message.warning('没有可移除的共享夹（需为所有者或具备「移除笔记」权限）');
      return;
    }
    try {
      for (const r of refs) {
        await removeRecordFromFolder(r.folderId, record._id);
      }
      message.success(refs.length > 1 ? `已从 ${refs.length} 个共享夹中移除` : '已移除');
      await loadFolderRecords();
      await fetchFolders();
    } catch (e: unknown) {
      message.error((e as Error).message || '移除失败');
    }
  };

  const buildCardMenu = (record: DisplayExperimentRecord): MenuProps['items'] => {
    const items: MenuProps['items'] = [
      {
        key: 'open',
        label: '打开详情',
        icon: <EyeOutlined />,
        onClick: () => navigate(`/workbench/experiment-records/${record._id}`)
      }
    ];

    const removableIds = selectedFolderId
      ? activeFolder && canRemoveInFolder(selectedFolderId)
        ? [selectedFolderId]
        : []
      : (record._sharedFolderRefs ?? []).filter((r) => canRemoveInFolder(r.folderId)).map((r) => r.folderId);

    if (removableIds.length === 1) {
      const fid = removableIds[0];
      items.push({ type: 'divider' });
      items.push({
        key: 'remove',
        danger: true,
        label: '从共享夹移除',
        icon: <DeleteOutlined />,
        onClick: () => {
          Modal.confirm({
            title: '确定从该共享夹中移除此笔记？',
            okText: '确定',
            cancelText: '取消',
            okType: 'danger',
            onOk: () => void handleRemoveSingle(record._id, fid)
          });
        }
      });
    } else if (removableIds.length > 1) {
      const names = (record._sharedFolderRefs ?? [])
        .filter((r) => removableIds.includes(r.folderId))
        .map((r) => r.folderName);
      items.push({ type: 'divider' });
      items.push({
        key: 'remove-all',
        danger: true,
        label: '从相关共享夹移除',
        icon: <DeleteOutlined />,
        onClick: () => {
          Modal.confirm({
            title: '从多个共享夹中移除此笔记？',
            content: `将在您有权限的 ${removableIds.length} 个共享夹中移除：${names.join('、')}`,
            okText: '全部移除',
            cancelText: '取消',
            okType: 'danger',
            onOk: () => void handleRemoveAllRefs(record)
          });
        }
      });
    }

    return items;
  };

  const recordColumns: ColumnsType<ExperimentRecord> = [
    { title: '标题', dataIndex: 'title', key: 'title', ellipsis: true },
    {
      title: '领域',
      dataIndex: 'discipline',
      key: 'discipline',
      width: 88,
      render: (d: ExperimentDiscipline) => DISCIPLINE_LABEL[d] || d
    },
    {
      title: '更新',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 150,
      render: (iso: string) => formatTime(iso)
    }
  ];

  const folderSelectOptions = useMemo(
    () => [
      { value: ALL_FOLDERS_VALUE, label: '全部共享夹' },
      ...folders.map((f) => ({
        value: f._id,
        label: `${f.name}${f.isOwner ? '（我的）' : '（共享给我）'}`
      }))
    ],
    [folders]
  );

  return (
    <div className="page-container full-height members-lims literature-lims-module er-page">
      <nav className="er-notebook-crumb" aria-label="breadcrumb">
        <button type="button" className="er-notebook-crumb-link" onClick={() => navigate('/')}>
          首页
        </button>
        <span className="material-symbols-outlined" aria-hidden>
          chevron_right
        </span>
        <button type="button" className="er-notebook-crumb-link" onClick={() => navigate('/workbench')}>
          工作台
        </button>
        <span className="material-symbols-outlined" aria-hidden>
          chevron_right
        </span>
        <button type="button" className="er-notebook-crumb-link" onClick={() => navigate('/workbench/experiment-records')}>
          实验记录本
        </button>
        <span className="material-symbols-outlined" aria-hidden>
          chevron_right
        </span>
        <span className="er-notebook-crumb-current">共享记录本</span>
      </nav>

      <header className="er-notebook-hero" style={{ marginBottom: '1rem' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 className="er-notebook-hero-title">共享记录本</h1>
          <p className="er-notebook-hero-sub">布局与文献「共享文献」一致：选择共享夹、邀请成员、管理权限与笔记列表。</p>
        </div>
        <div className="er-notebook-hero-actions">
          <button type="button" className="er-btn-filter-ghost" onClick={() => navigate('/workbench/experiment-records')}>
            全部记录
          </button>
          <button type="button" className="mm-btn-primary" onClick={() => setCreateOpen(true)}>
            <LimsNavIcon name="folder_shared" sizePx={20} />
            创建共享夹
          </button>
        </div>
      </header>

      {loading ? (
        <div className="mm-table-shell" style={{ textAlign: 'center', padding: 48 }}>
          <Text type="secondary">加载中...</Text>
        </div>
      ) : folders.length === 0 ? (
        <div className="mm-table-shell" style={{ padding: 48 }}>
          <Empty description="还没有共享夹，创建一个开始共享实验记录吧" />
        </div>
      ) : (
        <div className="lit-ant-table-wrap lit-lib-list-bg" style={{ padding: '8px 0 24px' }}>
          <div className="mm-table-shell lit-mm-table-shell--stitch" style={{ boxShadow: 'none', border: 'none', background: 'transparent' }}>
            <div className="mm-table-toolbar" style={{ marginBottom: 12 }}>
              <div className="lit-toolbar-inner">
                <div className="lit-toolbar-filters" style={{ width: '100%', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--st-on-surface, #191c1d)', marginRight: 8 }}>共享夹</span>
                  <Select
                    className="lit-toolbar-select"
                    showSearch
                    optionFilterProp="label"
                    style={{ minWidth: isMobile ? 200 : 280, maxWidth: '100%' }}
                    value={selectedFolderId ?? ALL_FOLDERS_VALUE}
                    options={folderSelectOptions}
                    onChange={(v) => setFolderFilter(v === ALL_FOLDERS_VALUE ? null : String(v))}
                  />
                </div>
              </div>
            </div>

            {activeFolder ? (
              <div
                style={{
                  marginBottom: 16,
                  padding: '12px 16px',
                  background: 'var(--st-surface-low, #f3f4f5)',
                  borderRadius: 'var(--mm-radius-xl, 16px)',
                  border: '1px solid rgba(194, 199, 209, 0.35)'
                }}
              >
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                  <Space wrap align="center">
                    <Text strong style={{ fontSize: 15 }}>
                      {activeFolder.name}
                    </Text>
                    {activeFolder.isOwner ? <Tag color="blue">我的</Tag> : <Tag color="green">共享给我的</Tag>}
                  </Space>
                  {activeFolder.description ? (
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      {activeFolder.description}
                    </Text>
                  ) : null}
                  <Space wrap>
                    <Tag>所有者: {activeFolder.owner?.username || activeFolder.owner?.email || '—'}</Tag>
                    <Tag>成员: {activeFolder.sharedUsers?.length ?? 0} 人</Tag>
                    <Tag>笔记: {displayRecords.length} 条</Tag>
                  </Space>
                  {activeFolder.isOwner ? (
                    <Space wrap>
                      <Button
                        type="default"
                        className="lit-stitch-btn-secondary"
                        size="small"
                        icon={<UserAddOutlined />}
                        onClick={() => {
                          setSelectedFolder(activeFolder);
                          setAddUserOpen(true);
                        }}
                      >
                        邀请用户
                      </Button>
                      <Button
                        type="default"
                        className="lit-stitch-btn-secondary"
                        size="small"
                        icon={<FileTextOutlined />}
                        onClick={() => void handleOpenAddRecords(activeFolder)}
                      >
                        添加笔记
                      </Button>
                      <Button
                        type="default"
                        className="lit-stitch-btn-secondary"
                        size="small"
                        icon={<SettingOutlined />}
                        onClick={() => handleOpenPermissions(activeFolder)}
                      >
                        权限设置
                      </Button>
                      <Popconfirm title="确定删除该共享夹？" onConfirm={() => void handleDeleteFolder(activeFolder._id)}>
                        <Button type="default" danger size="small" icon={<DeleteOutlined />}>
                          删除文件夹
                        </Button>
                      </Popconfirm>
                    </Space>
                  ) : null}
                  {!activeFolder.isOwner && (activeFolder.sharedUsers?.length ?? 0) > 0 ? (
                    <List
                      size="small"
                      header={<Text type="secondary">共享成员</Text>}
                      dataSource={activeFolder.sharedUsers}
                      renderItem={(u) => (
                        <List.Item style={{ padding: '4px 0' }}>
                          <List.Item.Meta
                            avatar={<Avatar icon={<ShareAltOutlined />} size="small" />}
                            title={u.user?.username || u.email}
                            description={u.email}
                          />
                          {(u as { status?: string }).status === 'pending' ? (
                            <Tag color="orange" icon={<ClockCircleOutlined />}>
                              待同意
                            </Tag>
                          ) : (
                            <Tag color="green" icon={<CheckCircleOutlined />}>
                              已加入
                            </Tag>
                          )}
                        </List.Item>
                      )}
                    />
                  ) : null}
                  {activeFolder.isOwner && (activeFolder.sharedUsers?.length ?? 0) > 0 ? (
                    <List
                      size="small"
                      header={<Text type="secondary">共享成员</Text>}
                      dataSource={activeFolder.sharedUsers}
                      renderItem={(u) => (
                        <List.Item
                          style={{ padding: '4px 0' }}
                          actions={[
                            (u as { status?: string }).status === 'pending' ? (
                              <Tag color="orange" icon={<ClockCircleOutlined />}>
                                待同意
                              </Tag>
                            ) : (
                              <Tag color="green" icon={<CheckCircleOutlined />}>
                                已加入
                              </Tag>
                            ),
                            (u as { status?: string }).status === 'joined' && u.user?._id ? (
                              <Button
                                type="link"
                                danger
                                size="small"
                                icon={<UserDeleteOutlined />}
                                onClick={() => void handleRemoveUser(activeFolder._id, u.user._id)}
                              >
                                移除
                              </Button>
                            ) : null
                          ]}
                        >
                          <List.Item.Meta
                            avatar={<Avatar icon={<ShareAltOutlined />} size="small" />}
                            title={u.user?.username || u.email}
                            description={u.email}
                          />
                        </List.Item>
                      )}
                    />
                  ) : null}
                </Space>
              </div>
            ) : (
              <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--mm-on-variant, #5c6370)' }}>
                当前为「全部共享夹」：同一条记录在多个夹中会合并为一条，标签展示来源。可在卡片菜单中从共享夹移除。
              </p>
            )}

            <Spin spinning={recLoading}>
              <div className="lit-lib-card-grid">
                {!recLoading && displayRecords.length === 0 ? (
                  <p style={{ gridColumn: '1 / -1', margin: 0, color: 'var(--mm-on-variant, #5c6370)' }}>
                    该范围下暂无笔记。选择共享夹后可使用「添加笔记」。
                  </p>
                ) : null}
                {displayRecords.map((record) => {
                  const snippet = recordSnippet(record);
                  const author =
                    (record.createdBy as { name?: string; username?: string } | undefined)?.name ||
                    (record.createdBy as { username?: string } | undefined)?.username ||
                    '—';
                  return (
                    <article
                      key={record._id + (record._sharedFolderHint || '')}
                      className="lit-lib-card"
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        const el = e.target as HTMLElement;
                        if (el.closest('.ant-dropdown-trigger')) return;
                        if (el.closest('button')) return;
                        navigate(`/workbench/experiment-records/${record._id}`);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          navigate(`/workbench/experiment-records/${record._id}`);
                        }
                      }}
                    >
                      <div className="lit-lib-card__top">
                        <div className="lit-lib-card__top-left">
                          {record._sharedFolderHint ? <Tag color="geekblue">{record._sharedFolderHint}</Tag> : null}
                          <Tag>{DISCIPLINE_LABEL[record.discipline] || record.discipline}</Tag>
                          <Tag color={record.status === 'final' ? 'success' : 'default'}>
                            {STATUS_LABEL[record.status] || record.status}
                          </Tag>
                        </div>
                        <div className="lit-lib-card__top-right">
                          <span className="lit-lib-card__time">{formatRelativeUpdated(record.updatedAt)}</span>
                          <Dropdown menu={{ items: buildCardMenu(record) }} trigger={['click']} placement="bottomRight">
                            <Button type="text" size="small" icon={<MoreOutlined />} title="更多" onClick={(ev) => ev.stopPropagation()} />
                          </Dropdown>
                        </div>
                      </div>
                      <h3 className="lit-lib-card__title">{record.title}</h3>
                      <p className="lit-lib-card__snippet">{snippet}</p>
                      <div className="lit-lib-card__meta">
                        <span className="lit-lib-card__meta-item">
                          <LimsNavIcon name="person" sizePx={16} />
                          {author}
                        </span>
                        <span className="lit-lib-card__meta-item">
                          <LimsNavIcon name="schedule" sizePx={16} />
                          {formatTime(record.updatedAt)}
                        </span>
                      </div>
                      <div className="lit-lib-card__actions" onClick={(ev) => ev.stopPropagation()}>
                        <Button
                          type="default"
                          size="small"
                          className="lit-stitch-btn-secondary lit-lib-card__pdf-btn"
                          icon={<EyeOutlined />}
                          onClick={(ev) => {
                            ev.stopPropagation();
                            navigate(`/workbench/experiment-records/${record._id}`);
                          }}
                        >
                          打开详情
                        </Button>
                      </div>
                    </article>
                  );
                })}
              </div>
            </Spin>
          </div>
        </div>
      )}

      <Modal
        title="创建共享夹"
        open={createOpen}
        onOk={() => void handleCreate()}
        onCancel={() => {
          setCreateOpen(false);
          setCreateName('');
          setCreateDesc('');
        }}
        confirmLoading={creating}
        okText="创建"
        cancelText="取消"
      >
        <div style={{ marginBottom: 16 }}>
          <Text strong>名称</Text>
          <Input
            placeholder="请输入共享夹名称"
            value={createName}
            onChange={(e) => setCreateName(e.target.value)}
            style={{ marginTop: 8 }}
            maxLength={100}
          />
        </div>
        <div>
          <Text strong>说明（可选）</Text>
          <TextArea
            placeholder="请输入说明"
            value={createDesc}
            onChange={(e) => setCreateDesc(e.target.value)}
            rows={3}
            style={{ marginTop: 8 }}
            maxLength={500}
          />
        </div>
      </Modal>

      <Modal
        title="邀请用户"
        open={addUserOpen}
        onOk={() => void handleInviteUser()}
        onCancel={() => {
          setAddUserOpen(false);
          setUserEmail('');
        }}
        okText="发送邀请"
        cancelText="取消"
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
          对方可在顶部通知或工作台「审批管理 → 待办事项」中接受后加入共享夹。
        </Text>
        <Input
          placeholder="请输入用户邮箱"
          value={userEmail}
          onChange={(e) => setUserEmail(e.target.value)}
          onPressEnter={() => void handleInviteUser()}
        />
      </Modal>

      <Modal
        title={`添加笔记到「${selectedFolder?.name}」`}
        open={addRecOpen}
        onOk={() => void handleAddRecords()}
        onCancel={() => {
          setAddRecOpen(false);
          setSelectedRecordIds([]);
        }}
        okText="添加"
        cancelText="取消"
        width={800}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12 }}>
          已在夹中的笔记默认勾选；可勾选更多以加入，取消勾选将从共享夹移除（不会删除实验记录本中的原文）。
        </Text>
        <Table
          columns={recordColumns}
          dataSource={allRecords}
          rowKey="_id"
          loading={loadingRecords}
          rowSelection={{
            selectedRowKeys: selectedRecordIds,
            onChange: (keys) => setSelectedRecordIds(keys as string[])
          }}
          pagination={false}
          scroll={{ y: 400 }}
        />
      </Modal>

      <Modal
        title={`权限设置 - 「${selectedFolder?.name}」`}
        open={permOpen}
        onOk={() => void handleSavePermissions()}
        onCancel={() => setPermOpen(false)}
        okText="保存"
        cancelText="取消"
        width={600}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <Title level={5}>协作者权限</Title>
            <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
              以下开关仅对「非所有者」的共享成员生效；所有者不受限制。
            </Text>
            <Divider style={{ margin: '12px 0' }} />
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div>
                  <Text strong>添加笔记</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    允许成员将本人创建的实验记录加入本共享夹
                  </Text>
                </div>
                <Switch
                  checked={permissions.canAddRecords}
                  onChange={(c) => setPermissions({ ...permissions, canAddRecords: c })}
                />
              </div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div>
                  <Text strong>移除笔记</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    允许成员从本共享夹中移除笔记（不删除实验记录本中的原文）
                  </Text>
                </div>
                <Switch
                  checked={permissions.canRemoveRecords}
                  onChange={(c) => setPermissions({ ...permissions, canRemoveRecords: c })}
                />
              </div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div>
                  <Text strong>查看成员列表</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    允许成员查看其他协作者
                  </Text>
                </div>
                <Switch checked={permissions.canViewUsers} onChange={(c) => setPermissions({ ...permissions, canViewUsers: c })} />
              </div>
            </div>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div>
                  <Text strong>编辑他人笔记</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    允许成员编辑共享夹内他人创建的实验记录
                  </Text>
                </div>
                <Switch
                  checked={!!permissions.canEditRecords}
                  onChange={(c) => setPermissions({ ...permissions, canEditRecords: c })}
                />
              </div>
            </div>
          </div>
        </Space>
      </Modal>
    </div>
  );
};

export default SharedExperimentNotebooks;
